import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  CONSENT_UI_EVENT,
  ConsentGate,
  expireCookiesByPrefix,
  reportConsentError,
  TIKTOK_COOKIE_PREFIXES,
} from '../consent/consent';
import { GoogleAdsService } from './google-ads.service';
import { MetaPixelService } from './meta-pixel.service';

/** The longest ready() makes a caller wait for the hosted script. */
export const CONSENT_READY_MAX_WAIT_MS = 2000;

/**
 * The shop's link to the consent banner igive.no serves
 * (environment.consentScriptUrl).
 *
 * start() loads the script in the background, after Angular has bootstrapped,
 * so checkout never waits on it: if it does not load there is no banner and,
 * because nothing calls the gate, nothing beyond Plausible and Sentry runs.
 *
 * Before loading it, start() wraps the gate from src/index.html so that the
 * script's apply() and revoke() reach the Meta pixel and Google Ads as well
 * as the GTM container. Those two load only where this shop allows them (own
 * cards and resold cards, the production host) and only when the script says
 * a yes covers them. A marketing no also expires TikTok's cookies, which
 * igive.no writes on the shared domain. The script calls the same gate for an
 * answer given in another tab, so a no there stops the tools here too.
 *
 * The rest bridges view trees: the footer's «Informasjonskapsler» button and
 * the customize-card preview widget render inside a page, the banner outside
 * the router outlet.
 */
@Injectable({ providedIn: 'root' })
export class ConsentUiService {
  /**
   * Whether the banner or its reload offer occupies the bottom-right corner.
   * Page-level floating affordances wait for it: ion-router-outlet is a
   * z-index:0 stacking context, so anything position:fixed inside a page is
   * painted below the banner however high its own z-index goes.
   *
   * Starts true, because until the script has run nobody knows whether it
   * will ask; a false start would flash the page's own affordance for the
   * moment before the banner claims the corner.
   */
  private chromeVisibleSubject = new BehaviorSubject<boolean>(true);
  readonly chromeVisible$: Observable<boolean> =
    this.chromeVisibleSubject.pipe(distinctUntilChanged());

  private bannerOpen = false;
  private started = false;
  private settled: Promise<void> = Promise.resolve();

  constructor(
    private router: Router,
    private metaPixel: MetaPixelService,
    private googleAds: GoogleAdsService
  ) {}

  /** Called once, from AppComponent. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.wrapGate();
    this.followChrome();
    this.settled = this.loadScript();
  }

  /**
   * Resolves once the hosted script has run or failed to load, never later
   * than `maxWaitMs`. The receipt waits on it before reporting a purchase,
   * because after the payment provider's full-page return nothing knows the
   * buyer's answer until the script has read it.
   */
  ready(maxWaitMs = CONSENT_READY_MAX_WAIT_MS): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return Promise.race([
      this.settled,
      new Promise<void>((resolve) => (timer = setTimeout(resolve, maxWaitMs))),
    ]).finally(() => clearTimeout(timer));
  }

  /**
   * Reopens the banner prefilled; focus returns to `trigger` after a decision.
   * Without the banner, the privacy policy where igive.no explains how to
   * change the choice, as igive.no's own link does.
   */
  requestReopen(trigger?: HTMLElement): void {
    if (window.igiveConsent) {
      window.igiveConsent.reopen(trigger ?? null);
      return;
    }
    const policy = `${new URL(environment.consentScriptUrl).origin}/personvern#endre-samtykke`;
    window.open(policy, '_blank', 'noopener');
  }

  /**
   * Extends the gate's apply() and revoke() to the marketing tools. Each
   * revoke() always runs, so `||` cannot skip a teardown; any tool still
   * executing in this document is reason enough to offer a reload.
   *
   * A marketing no also expires TikTok's cookies. Nothing of TikTok's runs in
   * the shop, so they need no tool of their own and never ask for a reload.
   *
   * No reload is offered on /payment, where reloading is an interruption on
   * the way to paying. The withdrawal itself has happened by then, and the
   * next page load finishes it.
   */
  private wrapGate(): void {
    const gate = window.__igiveConsentGate;
    const wrapped: ConsentGate = {
      apply: (scope) => {
        this.guard('consent.apply', () => gate?.apply(scope));
        this.guard('meta.apply', () => this.metaPixel.applyConsent());
        this.guard('googleAds.apply', () => this.googleAds.applyConsent());
      },
      revoke: (scope) => {
        const running = [
          this.guard('consent.revoke', () => gate?.revoke(scope)),
          this.guard('meta.revoke', () => this.metaPixel.revoke(scope)),
          this.guard('googleAds.revoke', () => this.googleAds.revoke(scope)),
        ].some((result) => result === true);
        if (!scope.includes('markedsforing')) {
          this.guard('tiktok.revoke.clearCookies', () => expireCookiesByPrefix(TIKTOK_COOKIE_PREFIXES));
        }
        return running && !this.router.url.startsWith('/payment');
      },
    };
    window.__igiveConsentGate = wrapped;
  }

  /**
   * Follows the script's open/close announcements. Added before the script is
   * inserted, because it announces an open banner while it runs.
   */
  private followChrome(): void {
    window.addEventListener(CONSENT_UI_EVENT, (event) => {
      this.bannerOpen = (event as CustomEvent<{ open?: unknown } | undefined>).detail?.open === true;
      this.chromeVisibleSubject.next(this.bannerOpen);
    });
  }

  private loadScript(): Promise<void> {
    return new Promise<void>((resolve) => {
      try {
        const script = document.createElement('script');
        script.async = true;
        script.src = environment.consentScriptUrl;
        // A script that has run without announcing an open banner opened nothing.
        script.addEventListener('load', () => {
          this.chromeVisibleSubject.next(this.bannerOpen);
          resolve();
        });
        script.addEventListener('error', () => {
          this.chromeVisibleSubject.next(false);
          resolve();
        });
        document.head.appendChild(script);
      } catch (e) {
        reportConsentError(e, 'consent.load');
        this.chromeVisibleSubject.next(false);
        resolve();
      }
    });
  }

  private guard<T>(tag: string, fn: () => T): T | undefined {
    try {
      return fn();
    } catch (e) {
      reportConsentError(e, tag);
      return undefined;
    }
  }
}
